import React, { Fragment, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Breadcrumbs, Grid, Typography } from "@material-ui/core";
import { makeStyles, createStyles, withStyles } from "@material-ui/core/styles";
import HomeStyles from "pages/HomeStyles";
import {
  getPictreePlotBreakdownData,
  getPictreePlotRankingData,
  getPictreePlotRewardData,
  getPictreePlotSummaryData,
  getPictreePlotTableData,
  getPictreePlotTrendData,
  getPictreePlotTrustData,
} from "actions";
import { useLocation } from "react-router-dom";
import * as C from "components";
import CalendarRangePickerPlot from "components/Calendar/CalendarRangePickerPlot";
import PlotAssetBreakdown from "components/PicTree/Plot/PlotAssetBreakdown";
import PlotAssetTable from "components/PicTree/Plot/PlotAssetTable";
import PlotSummary from "components/PicTree/Plot/PlotSummary";
import PlotRewardCoinStatus from "components/PicTree/Plot/PlotRewardCoinStatus";
import PlotTrustScore from "components/PicTree/Plot/PlotTrustScore";
import PlotTrendChart from "components/PicTree/Plot/PlotTrendChart";
import PlotUserRankingPreview from "components/PicTree/Plot/PlotUserRankingPreview";

const PLOT_START_DATE = "2026/08/01";

const useStyles = makeStyles((theme) =>
  createStyles({
    page: {
      paddingBottom: theme.spacing(3),
    },
    breadcrumbs: {
      marginTop: 16,
      minHeight: 30,
    },
    topRow: {
      marginTop: -16,
      marginBottom: 8,
      alignItems: "center",
    },
    chartGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.65fr) minmax(300px, 0.95fr)",
      gap: theme.spacing(2),
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
      },
    },
    bottomGrid: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1.25fr) minmax(0, 1fr)",
      gap: theme.spacing(2),
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
      },
    },
  })
);

const PicTreePlotOverview = ({
  getPlotSummaryData,
  getPlotTrustData,
  getPlotTrendData,
  getPlotBreakdownData,
  getPlotTableData,
  getPlotRankingData,
  getPlotRewardData,
  summaryItems,
  trustLevels,
  trendPoints,
  assetBreakdown,
  assetTable,
  userRankingRows,
  rewardSummaryCards,
  rewardHoldings,
  plotMunicipality,
  summaryLoading,
  trustLoading,
  trendLoading,
  breakdownLoading,
  tableLoading,
  rankingLoading,
  rewardLoading,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const pathname = location.pathname;
  const isOverviewPage = pathname === "/game/pictree/plot/overview";
  const [initialDataCall, setInitialDataCall] = useState(true);

  const loadOverviewData = useCallback(() => {
    getPlotTrendData();
    getPlotBreakdownData();
    getPlotTableData();
    getPlotRankingData();
    getPlotRewardData();
    getPlotTrustData();
    getPlotSummaryData();
  }, [
    getPlotBreakdownData,
    getPlotRankingData,
    getPlotRewardData,
    getPlotSummaryData,
    getPlotTableData,
    getPlotTrendData,
    getPlotTrustData,
  ]);

  useEffect(() => {
    if (!isOverviewPage) return;
    if (!initialDataCall) return;
    loadOverviewData();
    setInitialDataCall(false);
  }, [isOverviewPage, initialDataCall, loadOverviewData]);

  return (
    <Fragment>
      <C.ContentWrapper>
        <div className={classes.page}>
          <Grid container direction="row" justifyContent="space-between" className={classes.topRow}>
            <Grid item>
              <Breadcrumbs separator="›" className={classes.breadcrumbs}>
                <Typography>PicTrée</Typography>
                <Typography variant="body1">プロット機能</Typography>
                <Typography variant="body1">概要</Typography>
              </Breadcrumbs>
            </Grid>
            <Grid item>
              <CalendarRangePickerPlot
                mDate={PLOT_START_DATE}
                page="pictree"
                pictree
                timeselect={false}
                singleSelect={true}
                allowOutOfSeason={true}
                onGoClick={loadOverviewData}
              />
            </Grid>
          </Grid>

          <PlotSummary items={summaryItems} loading={summaryLoading} />

          <PlotTrustScore
            levels={trustLevels}
            subtitle={`適用中: ${plotMunicipality || "全体"}`}
            loading={trustLoading}
          />

          <div className={classes.chartGrid}>
            <PlotTrendChart points={trendPoints} loading={trendLoading} />
            <PlotAssetBreakdown items={assetBreakdown} loading={breakdownLoading} />
          </div>

          <PlotAssetTable
            rows={assetTable.rows}
            colors={assetTable.colors}
            summaryRow={assetTable.summaryRow}
            loading={tableLoading}
          />

          <div className={classes.bottomGrid}>
            <PlotUserRankingPreview rows={userRankingRows} loading={rankingLoading} />
            <PlotRewardCoinStatus
              summaryCards={rewardSummaryCards}
              holdings={rewardHoldings}
              loading={rewardLoading}
            />
          </div>
        </div>
      </C.ContentWrapper>
    </Fragment>
  );
};

PicTreePlotOverview.propTypes = {
  classes: PropTypes.object,
  getPlotSummaryData: PropTypes.func.isRequired,
  getPlotTrustData: PropTypes.func.isRequired,
  getPlotTrendData: PropTypes.func.isRequired,
  getPlotBreakdownData: PropTypes.func.isRequired,
  getPlotTableData: PropTypes.func.isRequired,
  getPlotRankingData: PropTypes.func.isRequired,
  getPlotRewardData: PropTypes.func.isRequired,
  summaryItems: PropTypes.array.isRequired,
  trustLevels: PropTypes.array.isRequired,
  trendPoints: PropTypes.array.isRequired,
  assetBreakdown: PropTypes.array.isRequired,
  assetTable: PropTypes.shape({
    rows: PropTypes.array.isRequired,
    summaryRow: PropTypes.object,
    colors: PropTypes.array.isRequired,
  }).isRequired,
  userRankingRows: PropTypes.array.isRequired,
  rewardSummaryCards: PropTypes.array.isRequired,
  rewardHoldings: PropTypes.shape({
    title: PropTypes.string.isRequired,
    rows: PropTypes.array.isRequired,
  }).isRequired,
  plotMunicipality: PropTypes.string.isRequired,
  summaryLoading: PropTypes.bool.isRequired,
  trustLoading: PropTypes.bool.isRequired,
  trendLoading: PropTypes.bool.isRequired,
  breakdownLoading: PropTypes.bool.isRequired,
  tableLoading: PropTypes.bool.isRequired,
  rankingLoading: PropTypes.bool.isRequired,
  rewardLoading: PropTypes.bool.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  getPlotSummaryData: () => dispatch(getPictreePlotSummaryData()),
  getPlotTrustData: () => dispatch(getPictreePlotTrustData()),
  getPlotTrendData: () => dispatch(getPictreePlotTrendData()),
  getPlotBreakdownData: () => dispatch(getPictreePlotBreakdownData()),
  getPlotTableData: () => dispatch(getPictreePlotTableData()),
  getPlotRankingData: () => dispatch(getPictreePlotRankingData()),
  getPlotRewardData: () => dispatch(getPictreePlotRewardData()),
});

export default connect(
  (state) => ({
    summaryItems: state.pictreePlot.summaryItems,
    trustLevels: state.pictreePlot.trustLevels,
    trendPoints: state.pictreePlot.trendPoints,
    assetBreakdown: state.pictreePlot.assetBreakdown,
    assetTable: state.pictreePlot.assetTable,
    userRankingRows: state.pictreePlot.userRankingRows,
    rewardSummaryCards: state.pictreePlot.rewardSummaryCards,
    rewardHoldings: state.pictreePlot.rewardHoldings,
    plotMunicipality: state.page.plotMunicipality,
    summaryLoading: state.pictreePlot.summaryLoading,
    trustLoading: state.pictreePlot.trustLoading,
    trendLoading: state.pictreePlot.trendLoading,
    breakdownLoading: state.pictreePlot.breakdownLoading,
    tableLoading: state.pictreePlot.tableLoading,
    rankingLoading: state.pictreePlot.rankingLoading,
    rewardLoading: state.pictreePlot.rewardLoading,
  }),
  mapDispatchToProps
)(withStyles(HomeStyles, { withTheme: true })(PicTreePlotOverview));
